import { Info, RefreshCw } from "lucide-react";
import { useContext, useEffect, useRef } from "react";

import { Controller } from "react-hook-form";
import CustomTooltip from "../ui-components/tooltip";
import { ThemeContext } from "@context/theme";
import classnames from "classnames";

const InputField = (props) => {
  const {
    errors,
    label = "",
    name,
    type = "text",
    id,
    placeholder = "",
    autoFocus = false,
    disabled = false,
    control,
    className = "",
    tooltipContent = "",
    required = false,
    multiple = false,
    customOnChange = () => {},
    handleBlur = () => {},
    prefix = {},
    suffix = {},
    isDirtyField = false,
    showErrMsg = true,
    desc = null,
    loading = false,
    inputClassName = "",
    enableBlurEffect = true,
    onFocus = () => {}, // Add onFocus prop
    onPaste = () => {},
    inputMode
  } = props;

  const message = errors && (errors[name]?.message || errors[id]?.message);
  /**
   * If there is an error message, return the error message
   * @returns message
   */
  const renderError = () => {
    return (
      message && (
        <small className="block text-danger" data-cy={`error-${id}`}>
          {message}
        </small>
      )
    );
  };

  const exceptThisSymbols = ["e", "E", "+", "-"];
  const isInvalid = errors && (errors[name] || errors[id]) && true;
  const { filterOpen } = useContext(ThemeContext);

  /** @type {import('react').MutableRefObject<HTMLInputElement|null>} */
  const inputRef = useRef(null);

  useEffect(() => {
    if (autoFocus) {
      setTimeout(() => {
        if (filterOpen) {
          inputRef?.current?.focus();
        } else {
          inputRef?.current?.blur();
        }
      }, 100);
    }
  }, [filterOpen]);

  const updatedSuffix = loading ? { icon: "loading", isLoading: true } : suffix;
  function removeLeadingInvisibleCharacters (str) {
    return str?.replace(/^[\p{Cf}\p{Z}]+/u, "");
  }

  return (
    <div
      className={classnames({
        "mb-1"     : !className,
        [className]: className
      })}
      data-tour={`input-${id}`}
    >
      {label && (
        <label htmlFor={name} data-cy={`label-${id}`}>
          {label}
          {required && <span className="font-bold text-danger">*</span>}
          {Boolean(tooltipContent) && (
            <>
              <Info size={16} className="ml-1 cursor-pointer text-secondary" />
              <CustomTooltip
                target={`tooltip-${id}`}
                content={tooltipContent}
              />
            </>
          )}
        </label>
      )}
      <Controller
        name={name}
        control={control}
        render={({ field: { value, onChange } }) => (
          <div
            className={classnames(
              "flex items-stretch overflow-hidden rounded border",
              isInvalid
                ? "border-danger"
                : isDirtyField
                  ? "border-warning"
                  : "border-gray-300 focus-within:border-primary"
            )}
            id={`error-${name.replaceAll(".", "-")}`}
          >
            {(prefix.txt || prefix.icon) && (
              <span
                className={classnames("flex items-center px-2 text-gray-400", {
                  "border-r border-danger": isInvalid
                })}
              >
                {prefix.txt}
                {prefix.icon && <prefix.icon size={16} />}
              </span>
            )}
            <input
              // @ts-ignore
              onWheel={(e) => e.target.blur()}
              ref={inputRef}
              type={type}
              inputMode={inputMode}
              placeholder={placeholder ? placeholder : `Enter ${label}`}
              id={name}
              multiple={multiple}
              className={classnames(
                "flex-1 border-0 bg-transparent px-3 py-2 text-sm focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-400",
                { [inputClassName]: inputClassName }
              )}
              onChange={(e) => {
                let nextValue = e.target.value;
                if (customOnChange) {
                  const filtered = customOnChange(nextValue);
                  if (typeof filtered === "string") nextValue = filtered;
                }
                const trimmedValue = removeLeadingInvisibleCharacters(
                  nextValue
                ); // Remove leading spaces only
                if (type === "number") {
                  onChange(trimmedValue || null);
                  return;
                }
                onChange(trimmedValue);
              }}
              onPaste={onPaste}
              onFocus={(e) => {
                if (onFocus) onFocus(e);
              }}
              onBlur={(e) => {
                if (enableBlurEffect) {
                  if (handleBlur) {
                    handleBlur();
                  } else {
                    if (customOnChange) customOnChange(e);
                    onChange(e);
                  }
                }
              }}
              min={0}
              onKeyDown={(e) => {
                if (type === "number") {
                  if (exceptThisSymbols.includes(e.key)) e.preventDefault();
                }
              }}
              value={value || value === 0 ? value : ""}
              autoFocus={autoFocus}
              disabled={disabled}
              data-cy={`input-${id}`}
            />
            {isInvalid && !showErrMsg && message && (
              <CustomTooltip
                target={`error-${name.replaceAll(".", "-")}`}
                content={message}
              />
            )}
            {(updatedSuffix.txt || updatedSuffix.icon) && (
              <span
                className={classnames(
                  "flex items-center gap-1 border-l px-2",
                  isInvalid
                    ? "border-danger"
                    : isDirtyField
                      ? "border-0"
                      : "border-gray-300"
                )}
              >
                {updatedSuffix.txt ? (
                  <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                    {updatedSuffix.txt}
                  </span>
                ) : null}
                {updatedSuffix.isLoading ? (
                  <RefreshCw size={16} className="animate-spin" />
                ) : updatedSuffix.icon ? (
                  <updatedSuffix.icon size={16} />
                ) : null}
              </span>
            )}
          </div>
        )}
      />
      {showErrMsg && renderError()}
      {desc && (
        <p>
          <small className="font-bold" data-cy="input-desc">
            <i>{desc}</i>
          </small>
        </p>
      )}
    </div>
  );
};

export default InputField;
